import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ContactEmail,
  ContactPhone,
  Loading,
  Modal,
  Section,
  SelectDropdownField,
  Text,
} from '@tradingblock/components';
import _ from 'lodash';
import { PageTitle } from '../components';
import { ApplicationLoader } from '../components/application';
import { getApplicationStepUrl } from '../services';
import { useData } from '../state/selectors';
import { DefaultApplicationModel } from '../types';
import { useDispatcher } from '../components/hooks';
import { ApplicationActions } from '../state/actions';
import InvestigationUpload from '../components/fields/InvestigationUpload';
import { UploadedDocuments } from '../components/values';
import { getRequestedParticipantsDocuments } from '@tradingblock/api';
import { Modal as BootstrapModal, Button, FormGroup } from 'react-bootstrap';
import { Field, Form, Formik, FormikProps } from 'formik';
import { ApiResponse, InvestigationDocuments, ResponseCodes } from '@tradingblock/types';
import jsPDF from 'jspdf';
import { useApi } from '../context';

interface CameraFormValues {
  selectedDocument: InvestigationDocuments;
}

const StatusPage: React.FC = () => {
  const isRnWebView = window.isRnWebView;

  const dispatch = useDispatcher();
  const api = useApi();

  const siteGrpParam = process.env.REACT_APP_TB_SITE_GROUP;
  const authToken = useData(d => d.ui.apiToken);
  const authTokenExpired = useData(d => d.ui.apiTokenExpired);
  const application = useData(d => d.data.application);
  const applicationStatus = useData(d => d.data.status);
  const applicationStatusIsFetching = useData(d => d.data.isFetchingStatus);

  const { accountId, clearer } = application || DefaultApplicationModel;
  const { state, investigations, applications } = applicationStatus || {
    state: undefined,
    investigations: [],
    applications: [],
  };

  // Reduce and union both investigations and applications requested documents
  const requestedParticipantsDocuments = useMemo(() => {
    return getRequestedParticipantsDocuments(investigations, applications);
  }, [investigations, applications]);

  // boolean to check if we have any requested documents
  const areDocumentsRequested = useMemo(() => {
    return requestedParticipantsDocuments.some(
      participant =>
        participant.requestedCatagories.investigations.length > 0 ||
        participant.requestedCatagories.application.length > 0
    );
  }, [requestedParticipantsDocuments]);

  useEffect(() => {
    // check for application status if account already created
    if (accountId && authToken && !authTokenExpired) {
      dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
    }
  }, [accountId, dispatch, authToken, authTokenExpired]);

  //#region Section for utilizing media devicees to upload for required documents
  const formatDocumentName = (documentName: string) => {
    const specialCases: { [key: string]: string } = {
      DriversLicense: "Driver's License",
      StateIdCard: 'State ID Card',
      Passport: 'Passport',
      MilitaryId: 'Military ID',
      SsnCard: 'SSN Card',
      SsaLetter: 'SSA Letter',
      IrsItinLetter: 'IRS ITIN Letter',
      OtherGovernmentId: 'Other Government ID',
      CddDocument: 'CDD Document',
      AllPassingCipResults: 'All Passing CIP Results',
      IrsW9Form: 'IRS W-9 Form',
      SignatureImage: 'Signature Image',
      IdDocument: 'ID Document',
      AffiliateApproval: 'Affiliate Approval',
      Document: 'Document',
      Other: 'Other',
      GovernmentId: 'Government ID',
    };

    if (specialCases[documentName]) {
      return specialCases[documentName];
    }

    return (
      documentName
        // Add spaces before each capital letter except the first one
        .replace(/([A-Z])/g, ' $1')
        // Trim any leading spaces
        .trim()
        // Convert to title case
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
    );
  };

  const [tempBlob, setTempBlob] = useState<Blob | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const photoRef = useRef<HTMLCanvasElement | null>(null);
  const frontPhotoRef = useRef<HTMLCanvasElement | null>(null);
  const backPhotoRef = useRef<HTMLCanvasElement | null>(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const [cameraErrored, setCameraErrored] = useState(false);
  const [cameraExists, setCameraExists] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(true);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [errorState, setErrorState] = useState<string>('');
  const [loadingError, setLoadingError] = useState<string>('');
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(true);
  const [requestedDocuments, setRequestedDocuments] = useState<InvestigationDocuments[]>([]);
  const [appInvList, setAppInvList] = useState<
    { id: number; type: string; docs: InvestigationDocuments[]; name: string }[]
  >([]);
  const [selectedDocumentName, setSelectedDocumentName] = useState<InvestigationDocuments>(
    InvestigationDocuments.DriversLicense
  );
  const [selectedDocumentSectionList, setSelectedDocumentSectionList] = useState<
    { id: number; type: string; docs: InvestigationDocuments[]; name: string }[]
  >([]);
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);

  useEffect(() => {
    setDisabled(uploading);
  }, [uploading]);

  const timeoutMs = 10000;
  function withTimeout<T>(promise: Promise<T>): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Operation timed out'));
      }, timeoutMs);

      promise.finally(() => clearTimeout(timer)); // Clear the timer when the promise settles
    });

    return Promise.race([promise, timeoutPromise]);
  }

  useEffect(() => {
    if (!openCameraModal) stopVideo();
    else {
      setHasPhoto(false);
      setFrontImage(null);
      setBackImage(null);
      setIsDisabledSubmit(true);
    }
  }, [openCameraModal]);

  // if a user has selected a document that requires a front and back image, and they have not uploaded both images, but they change the selected document, we should clear the images
  useEffect(() => {
    if (isIdCard) {
      if (frontImage && backImage) {
        setIsDisabledSubmit(false);
        viewPhoto();
        viewPhoto('back');
      } else setIsDisabledSubmit(true);
    } else if (frontImage) {
      setIsDisabledSubmit(false);
      viewPhoto();
    } else setIsDisabledSubmit(true);
  }, [frontImage, backImage]);

  const isIdCard =
    selectedDocumentName === InvestigationDocuments.StateIdCard ||
    selectedDocumentName === InvestigationDocuments.DriversLicense;

  const renderImageToCanvas = async (imageSrc: any) => {
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => (img.onload = resolve));
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, img.width, img.height);
    }

    URL.revokeObjectURL(imageSrc);
    return canvas;
  };

  // maxFileSize variable set to 12MB
  const maxFileSize = 12 * 1024 * 1024;

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF();

      // Render front image to canvas
      if (frontImage) {
        const frontImageURL = URL.createObjectURL(frontImage);
        const frontCanvas = await renderImageToCanvas(frontImageURL);
        const frontImgData = frontCanvas.toDataURL('image/jpeg');
        const frontImgWidth = frontCanvas.width;
        const frontImgHeight = frontCanvas.height;

        // Maintain aspect ratio for front image
        const frontAspectRatio = frontImgWidth / frontImgHeight;
        const frontNewWidth = 150; // Desired width
        const frontNewHeight = frontNewWidth / frontAspectRatio;

        pdf.addImage(frontImgData, 'JPEG', 15, 40, frontNewWidth, frontNewHeight);
      }

      if (backImage) {
        // Add a new page
        pdf.addPage();
        // Render back image to canvas
        const backImageURL = URL.createObjectURL(backImage);
        const backCanvas = await renderImageToCanvas(backImageURL);
        const backImgData = backCanvas.toDataURL('image/jpeg');
        const backImgWidth = backCanvas.width;
        const backImgHeight = backCanvas.height;

        // Maintain aspect ratio for back image
        const backAspectRatio = backImgWidth / backImgHeight;
        const backNewWidth = 150; // Desired width
        const backNewHeight = backNewWidth / backAspectRatio;
        pdf.addImage(backImgData, 'JPEG', 15, 40, backNewWidth, backNewHeight);
      }

      // Generate PDF Blob and display
      const pdfBlob = pdf.output('blob');

      // check size of the pdfBlob
      if (pdfBlob.size > maxFileSize) {
        alert('The combined image is too large to upload. Please try again with smaller images.');
        return;
      }

      // return a new file object with the pdfBlob
      return new File([pdf.output('blob')], 'combined.pdf', { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleUpload = async () => {
    const appOrInv = appInvList.find(item => item.docs.includes(selectedDocumentName));
    if (appOrInv) {
      try {
        const fileToUpload = await generatePDF();
        if (fileToUpload) {
          let response;
          if (appOrInv.type === 'app' && appOrInv.id !== 0 && accountId) {
            setOpenCameraModal(false);
            setUploading(true);
            response = await withTimeout(
              api.application
                .uploadInvestigationDocument(fileToUpload, accountId, appOrInv.id, selectedDocumentName)
                .then(response => {
                  processUploadResponse(response);
                })
            );
          } else if (appOrInv.type === 'inv' && appOrInv.id !== 0 && accountId) {
            setOpenCameraModal(false);
            setUploading(true);
            response = await withTimeout(
              api.application
                .uploadInvestigationDocument(fileToUpload, accountId, appOrInv.id, selectedDocumentName)
                .then(response => {
                  processUploadResponse(response);
                })
            );
          }
        } else {
          throw new Error('File to upload is undefined.');
        }
      } catch (error) {
        setUploading(false);
        setErrorState('An error occurred while processing the upload.');
      }
    } else {
      setErrorState('Can not find matching document');
    }
  };

  const fetchStatus = (accountId: number) => {
    setSuccess(false);
    setRequestedDocuments([]);
    setAppInvList([]);
    checkForCamera();
    dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
  };

  //Processes the upload response from the api
  const processUploadResponse = (response: ApiResponse<number>) => {
    setUploading(false);
    // check if the upload was not successful and set the errors/initial states.
    if (response.responseCode !== 0) {
      setErrorState(ResponseCodes[response.responseCode].description as string);
    } else {
      setSuccess(true);
      // There were no errors, reset the states and set success to true
      setErrorState('');
      const fetchStatusTimer = setTimeout(fetchStatus, 1500, accountId);
      return () => clearTimeout(fetchStatusTimer);
    }
  };

  // Function to check for available cameras
  const checkForCamera = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(device => device.kind === 'videoinput');
        setCameraExists(hasVideoInput); // Update state based on presence of camera
      }
    } catch (error) {
      console.error('Error checking for camera:', error);
    }
  };

  async function checkCameraPermission() {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });

      if (result.state === 'granted') {
        setCameraExists(true);
      } else if (result.state === 'denied') {
        setCameraExists(false);
        setCameraPermission(false);
      }

      // Listen for changes in permission state
      result.onchange = () => {
        if (result.state === 'denied') {
          setCameraExists(false);
          setCameraPermission(false);
        }
      };
    } catch (error) {
      console.log('Error checking camera permission:', error);
    }
  }

  useEffect(() => {
    checkForCamera();
    checkCameraPermission();
  }, []); // Runs once on component mount

  const currentTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (cameraLoaded) {
      clearTimeout(currentTimeout.current as NodeJS.Timeout);
    }
  }, [cameraLoaded, currentTimeout]);

  const viewPhoto = (side: string = 'front') => {
    const image = side === 'front' ? frontImage : backImage;
    if (image) {
      const imageUrl = URL.createObjectURL(image);

      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const canvas = side === 'front' ? frontPhotoRef.current : backPhotoRef.current;
        if (canvas) {
          const context = canvas.getContext('2d');
          // Calculate new dimensions to maintain aspect ratio
          let photowidth = 414;
          let photoheight = 414 / (1920 / 1080);

          // Set canvas size
          canvas.width = photowidth;
          canvas.height = photoheight;

          // Draw the image onto the canvas
          if (context) {
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            context.drawImage(img, 0, 0, 1920, 1080, 0, 0, photowidth, photoheight);
          }

          // Release the object URL (optional, for memory management)
          URL.revokeObjectURL(imageUrl);
        }
      };

      img.onerror = () => {
        console.error('Failed to load image.');
        URL.revokeObjectURL(imageUrl);
      };
      setHasPhoto(true);
    }
  };

  const getVideo = useCallback(
    async (counter: number = 3) => {
      try {
        checkCameraPermission();
        setCameraLoaded(false);
        setLoadingError('');

        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
          },
        });

        let video = videoRef.current;
        if (video && streamRef.current) {
          video.srcObject = streamRef.current;
          video.play();
          video.onloadeddata = () => {
            setCameraLoaded(true);
          };

          if (counter > 0) {
            currentTimeout.current = setTimeout(() => {
              console.log('Retrying media capture. Try Count: ', counter);
              getVideo(counter - 1);
              currentTimeout.current = null;
            }, 3000);
            return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
          } else {
            setCameraErrored(true);
            setCameraExists(false);
          }
        }
      } catch (err) {
        console.error(err);

        if (err.name === 'NotReadableError') {
          setLoadingError(
            'Webcam/Camera is already in use. To upload with Webcam/Camera, please reset the Webcam/Camera and try again.'
          );
        } else if ((counter > 0 && !(err.name === 'NotAllowedError')) || err.name === 'PermissionDeniedError') {
          currentTimeout.current = setTimeout(() => {
            console.log('Retrying media capture. Try Count: ', counter);
            getVideo(counter - 1);
            currentTimeout.current = null;
          }, 3000);
          return () => clearTimeout(currentTimeout.current as NodeJS.Timeout);
        } else {
          setCameraErrored(true);
          setCameraExists(false);
        }
      }
    },
    [cameraPermission]
  );

  function stopVideo() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  }

  const takePhoto = () => {
    const width = 1920;
    const height = 1080;

    let video = videoRef.current;
    let photo = photoRef.current;

    if (video && photo) {
      photo.width = width;
      photo.height = height;

      let context = photo.getContext('2d');
      if (context) {
        // Enable smooth scaling
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        context.drawImage(video, 0, 0);
        setHasPhoto(true);

        photo.toBlob(blob => {
          if (blob) {
            setTempBlob(blob);
          }
        }, 'image/jpeg');

        photo.width = 414;
        photo.height = 414 / (1920 / 1080);

        context.drawImage(video, 0, 0, width, height, 0, 0, photo.width, photo.height);
      }
    }
  };

  const retakePhoto = (backPhoto = false) => {
    let photo = photoRef.current;
    if (photo) {
      let context = photo.getContext('2d');
      if (context) {
        context.clearRect(0, 0, photo.width, photo.height);
        setHasPhoto(false);
        if (!backPhoto) setFrontImage(null);
        else setBackImage(null);
      }
    }
  };

  const clearPhoto = (backPhoto = false) => {
    let photo = !backPhoto ? frontPhotoRef.current : backPhotoRef.current;
    if (photo) {
      let context = photo.getContext('2d');
      if (context) {
        context.clearRect(0, 0, photo.width, photo.height);
        setHasPhoto(false);
        if (!backPhoto) setFrontImage(null);
        else setBackImage(null);
      }
    }
  };

  const keepPhoto = (backPhoto = false) => {
    if (tempBlob) {
      const imageName = isIdCard
        ? !backPhoto
          ? 'front-' + selectedDocumentName + '.jpg'
          : 'back-' + selectedDocumentName + '.jpg'
        : selectedDocumentName + '.jpg';

      // Create a File object from the Blob
      if (!backPhoto) setFrontImage(new File([tempBlob], imageName, { type: 'image/jpeg' }));
      else setBackImage(new File([tempBlob], imageName, { type: 'image/jpeg' }));
      setHasPhoto(false);
      setTempBlob(null);
    }
  };

  //#endregion

  return (
    <ApplicationLoader defaultRedirect="/">
      <PageTitle page="status" />
      <Section>
        <div className="step-title">
          <h1>
            <Text id="title" page="status" />
          </h1>
        </div>
      </Section>
      {applicationStatusIsFetching && (
        <Section>
          <Loading />
        </Section>
      )}
      {!applicationStatusIsFetching && (
        <Section>
          {!authTokenExpired ? (
            <>
              {(state === 'InvestigationSubmitted' ||
                state === 'ReadyForBackOffice' ||
                state === 'BackOffice' ||
                state === 'AccountSetup') && (
                <>
                  <h4 className="my-4">
                    <Text id="current-status" page="status" />:{' '}
                    <strong>
                      <Text id={`state.${state}`} page="status" />
                    </strong>
                  </h4>
                  <p>
                    <Text id="inProcess" page="status" />
                  </p>
                  <br />
                </>
              )}
              {(state === 'PendingSubmit' ||
                state === 'ActionRequired' ||
                state === 'Suspended' ||
                state === 'Pending') && (
                <>
                  {!areDocumentsRequested && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={`state.${state}`} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="inProcess" page="status" />
                      </p>
                      <br />
                    </>
                  )}
                  {areDocumentsRequested && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={`state.${state}`} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="moreInfoNeededDescription" page="status" />
                      </p>
                      <br />
                    </>
                  )}
                </>
              )}
              {(state === 'Error' || state === 'Cancelled' || state === 'Rejected') && (
                <>
                  <h4 className="my-4">
                    <Text id="current-status" page="status" />:{' '}
                    <strong>
                      <Text id={`state.${state}`} page="status" />
                    </strong>
                  </h4>
                  <p>
                    <Text id="accountRejected" page="status" />
                  </p>
                  <br />
                </>
              )}
              {state === 'Complete' && (
                <>
                  <h4 className="my-4">
                    <Text id="current-status" page="status" />:{' '}
                    <strong>
                      <Text id={`state.${state}`} page="status" />
                    </strong>
                  </h4>
                  <p>
                    <Text id="activeAccount" page="status" />
                  </p>
                  <br />
                </>
              )}
              {state === undefined && (
                <>
                  {!areDocumentsRequested && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={`state.PartialEntitySubmission`} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="inProcess" page="status" />
                      </p>
                      <br />
                    </>
                  )}
                  {areDocumentsRequested && (
                    <>
                      <h4 className="my-4">
                        <Text id="current-status" page="status" />:{' '}
                        <strong>
                          <Text id={`state.PartialEntitySubmission`} page="status" />
                        </strong>
                      </h4>
                      <p>
                        <Text id="moreInfoNeededDescription" page="status" />
                      </p>
                      <br />
                    </>
                  )}
                </>
              )}
              {(siteGrpParam === 'tb' || !siteGrpParam) && (
                <>
                  <Text
                    id="contact.alternate"
                    siteGroup={siteGrpParam}
                    data={{
                      email: <ContactEmail siteGroup={siteGrpParam} />,
                      phone: <ContactPhone siteGroup={siteGrpParam} />,
                    }}
                  />
                  <br />
                  <Text id="contact.phone.TradingBlockInternational" />
                  <br />
                </>
              )}

              {siteGrpParam === 'mb' && (
                <Text
                  id="contact.alternate"
                  siteGroup={siteGrpParam}
                  data={{
                    email: <ContactEmail siteGroup={siteGrpParam} />,
                    phone: <ContactPhone siteGroup={siteGrpParam} />,
                  }}
                />
              )}
              <br />

              {cameraExists && openCameraModal && (
                <>
                  <Modal setShow={setOpenCameraModal} show={openCameraModal} aria-labelledby="cameraModal">
                    <BootstrapModal.Header closeButton>
                      <BootstrapModal.Title>Webcam Capture</BootstrapModal.Title>
                    </BootstrapModal.Header>
                    <BootstrapModal.Body>
                      <Formik
                        initialValues={{
                          selectedDocument: requestedDocuments[0],
                        }}
                        as
                        CameraFormValues
                        onSubmit={() => {}}
                      >
                        {({ setFieldValue }: FormikProps<CameraFormValues>) => (
                          <Form translate={'yes'} id="form">
                            <FormGroup>
                              <Field
                                id={'selectedDocument'}
                                component={SelectDropdownField}
                                label="Requested Documents"
                                options={_.map(requestedDocuments, id => id)}
                                rawOptionLabels={requestedDocuments.reduce(
                                  (acc, item: InvestigationDocuments) => {
                                    acc[item] = formatDocumentName(item);
                                    return acc;
                                  },
                                  {} as Record<string, string>
                                )}
                                useRawOptions={true}
                                errorImmediately={true}
                                onChanged={(val: InvestigationDocuments) => {
                                  setFieldValue('selectedDocument', val);
                                  setFrontImage(null);
                                  setBackImage(null);
                                  setIsDisabledSubmit(true);
                                  setHasPhoto(false);
                                  setSelectedDocumentName(val);
                                  setSelectedDocumentSectionList(appInvList.filter(item => item.docs.includes(val)));
                                }}
                              />
                            </FormGroup>
                            <FormGroup>
                              <span
                                className="mute"
                                style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}
                              >
                                The following section(s) will be uploaded with the requested document(s):
                              </span>
                              <ul className="section-list">
                                {_.uniqBy(selectedDocumentSectionList, 'name').map((section, index) => {
                                  return (
                                    <li className="item" key={index}>
                                      <strong>{formatDocumentName(section.name)}</strong>
                                    </li>
                                  );
                                })}
                              </ul>
                            </FormGroup>
                            <div style={{ marginTop: '.5rem' }}>
                              <div className={!hasPhoto ? 'camera' : 'camera hidden'}>
                                {!cameraLoaded && (
                                  <div
                                    style={{
                                      width: 414,
                                      height: 414 / (16 / 9),
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {loadingError ? <strong>{loadingError}</strong> : <Loading />}
                                  </div>
                                )}
                                <video
                                  ref={videoRef}
                                  className="cameraCanvas"
                                  style={cameraLoaded && isDisabledSubmit ? { display: 'block' } : { display: 'none' }}
                                ></video>
                                {isIdCard && cameraLoaded && (
                                  <>
                                    {!frontImage && (
                                      <Button variant={'info'} className="mt-2" onClick={takePhoto}>
                                        Take Front Photo
                                      </Button>
                                    )}
                                    {frontImage && !backImage && (
                                      <Button variant={'info'} className="mt-2" onClick={takePhoto}>
                                        Take Back Photo
                                      </Button>
                                    )}
                                  </>
                                )}
                                {!isIdCard && cameraLoaded && !frontImage && (
                                  <Button variant={'info'} className="mt-2" onClick={takePhoto}>
                                    Take Photo
                                  </Button>
                                )}
                              </div>
                              <div className={hasPhoto ? 'result hasPhoto' : 'result'}>
                                <canvas
                                  className="cameraCanvas"
                                  ref={photoRef}
                                  style={{ display: isDisabledSubmit ? 'block' : 'none' }}
                                ></canvas>
                                <canvas
                                  className="cameraCanvas"
                                  ref={frontPhotoRef}
                                  style={{ display: !isDisabledSubmit ? 'block' : 'none' }}
                                ></canvas>
                                <canvas
                                  className="cameraCanvas"
                                  ref={backPhotoRef}
                                  style={{
                                    display: !isDisabledSubmit && isIdCard ? 'block' : 'none',
                                    paddingTop: '15px',
                                  }}
                                ></canvas>
                                {!isDisabledSubmit && (
                                  <>
                                    {frontImage && (
                                      <Button variant={'outline-warning'} className="mt-2" onClick={() => clearPhoto()}>
                                        Clear {backImage && <>Front</>} Photo
                                      </Button>
                                    )}
                                    {backImage && (
                                      <Button
                                        variant={'outline-warning'}
                                        className="mt-2"
                                        onClick={() => clearPhoto(true)}
                                      >
                                        Clear Back Photo
                                      </Button>
                                    )}
                                  </>
                                )}
                                {isIdCard && cameraLoaded && (
                                  <>
                                    {!frontImage && (
                                      <>
                                        <Button variant={'info'} className="mt-2" onClick={() => keepPhoto(false)}>
                                          Keep
                                        </Button>
                                        <Button
                                          variant={'outline-warning'}
                                          className="mt-2"
                                          onClick={() => retakePhoto(false)}
                                        >
                                          Retake
                                        </Button>
                                      </>
                                    )}
                                    {frontImage && !backImage && (
                                      <>
                                        <Button variant={'info'} className="mt-2" onClick={() => keepPhoto(true)}>
                                          Keep
                                        </Button>
                                        <Button
                                          variant={'outline-warning'}
                                          className="mt-2"
                                          onClick={() => retakePhoto(true)}
                                        >
                                          Retake
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                                {!isIdCard && cameraLoaded && (
                                  <>
                                    {!frontImage && (
                                      <>
                                        <Button variant={'info'} className="mt-2" onClick={() => keepPhoto(false)}>
                                          Keep
                                        </Button>
                                        <Button
                                          variant={'outline-warning'}
                                          className="mt-2"
                                          onClick={() => retakePhoto(false)}
                                        >
                                          Retake
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                                {!isDisabledSubmit && (
                                  <Button
                                    variant={'primary'}
                                    className="mt-2"
                                    onClick={() => {
                                      if (!isDisabledSubmit) {
                                        handleUpload();
                                      }
                                    }}
                                  >
                                    Submit following documents
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                      <Button variant={'secondary'} onClick={() => setOpenCameraModal(false)}>
                        Close
                      </Button>
                    </BootstrapModal.Footer>
                  </Modal>
                </>
              )}

              {requestedParticipantsDocuments.map(
                (participant, index) =>
                  (participant.requestedCatagories.application.length > 0 ||
                    participant.requestedCatagories.investigations.length > 0 ||
                    participant.uploadedDocuments.length > 0) && (
                    <div key={`${participant.firstName}-${index}`}>
                      {participant.uploadedDocuments.length > 0 && (
                        <>
                          <h4 className="my-4">
                            <Text
                              id={'documentsUploadedForPerson'}
                              page="status"
                              data={{
                                firstName: participant.firstName,
                                middleName: participant.middleName,
                                lastName: participant.lastName,
                              }}
                            />
                          </h4>
                          <ul>
                            {participant.uploadedDocuments.map((uploadedDocument, index) => (
                              <React.Fragment key={'application_uploaded_document' + index}>
                                <li>
                                  <UploadedDocuments value={uploadedDocument} />
                                </li>
                                <br />
                              </React.Fragment>
                            ))}
                          </ul>
                        </>
                      )}
                      {(participant.requestedCatagories.application.length > 0 ||
                        participant.requestedCatagories.investigations.length > 0) && (
                        <>
                          <h4 className="my-4">
                            <Text
                              id={`moreInfoNeededForPerson`}
                              page="status"
                              data={{
                                firstName: participant.firstName,
                                middleName: participant.middleName,
                                lastName: participant.lastName,
                              }}
                            />
                          </h4>

                          {cameraPermission && !cameraErrored && cameraExists && areDocumentsRequested && (
                            <>
                              <Button
                                key={`button-${index}`}
                                className={'btn-btn btn-primary mb-4 hide-ipad-or-smaller'}
                                disabled={disabled}
                                onClick={() => {
                                  setOpenCameraModal(!openCameraModal);
                                  let letRequestedDocuments: InvestigationDocuments[] = [];
                                  let letAppInvList: {
                                    id: number;
                                    type: string;
                                    docs: InvestigationDocuments[];
                                    name: string;
                                  }[] = [];
                                  if (participant.requestedCatagories.application.length > 0) {
                                    participant.requestedCatagories.application.map(application => {
                                      letRequestedDocuments = [
                                        ...new Set([...letRequestedDocuments, ...application.requestedDocuments]),
                                      ];
                                      letAppInvList = [
                                        ...new Set([
                                          ...letAppInvList,
                                          {
                                            id: participant.accountHolderId ? participant.accountHolderId : 0,
                                            type: 'app',
                                            docs: application.requestedDocuments,
                                            name: application.name,
                                          },
                                        ]),
                                      ];
                                    });
                                  }

                                  if (participant.requestedCatagories.investigations.length > 0) {
                                    participant.requestedCatagories.investigations.map(investigation => {
                                      letRequestedDocuments = [
                                        ...new Set([...letRequestedDocuments, ...investigation.requestedDocuments]),
                                      ];
                                      letAppInvList = [
                                        ...new Set([
                                          ...letAppInvList,
                                          {
                                            id: participant.investigationId ? participant.investigationId : 0,
                                            type: 'inv',
                                            docs: investigation.requestedDocuments,
                                            name: investigation.name,
                                          },
                                        ]),
                                      ];
                                    });
                                  }
                                  setRequestedDocuments(letRequestedDocuments);
                                  setAppInvList(letAppInvList);
                                  setSelectedDocumentName(letRequestedDocuments[0]);
                                  setSelectedDocumentSectionList(
                                    letAppInvList.filter(item => item.docs.includes(letRequestedDocuments[0]))
                                  );
                                  if (!openCameraModal) getVideo();
                                }}
                              >
                                Upload with Webcam/Camera
                              </Button>
                            </>
                          )}

                          {success && (
                            <div className=" mb-4">
                              <i className="fas fa-check-circle text-success" />{' '}
                              <Text id={'investigationUpload.uploadSuccessful'} type={'field'} />
                            </div>
                          )}
                          {uploading && (
                            <div className=" mb-4">
                              <i className="fas fa-circle-notch fa-spin valid" />{' '}
                              <Text id={'investigationUpload.uploading'} type={'field'} />
                            </div>
                          )}
                          {errorState && <div className="error mb-4">{errorState}</div>}

                          {!cameraPermission && (
                            <div className="mb-4">
                              <strong>
                                Webcam/Camera Permission has been denied. To upload with Webcam/Camera, please
                                reset/allow the perimission and refresh the page.
                              </strong>
                            </div>
                          )}
                          {cameraErrored && (
                            <div className="mb-4">
                              <strong>
                                Webcam/Camera has failed to load. Upload with Webcam/Camera is disabled. Sorry for the
                                inconveneince.
                              </strong>
                            </div>
                          )}

                          <ul>
                            {participant.requestedCatagories.application.map((requestedDocument, index) => (
                              <li key={'application_requested_document' + index}>
                                <InvestigationUpload
                                  accountId={accountId ? accountId : -1}
                                  accountHolderId={participant.accountHolderId}
                                  requestedInvestigationDocument={requestedDocument}
                                  type={'application'}
                                  clearer={clearer}
                                  disabled={disabled}
                                  setDisabled={setDisabled}
                                />
                              </li>
                            ))}
                            {participant.requestedCatagories.investigations.map((requestedDocument, index) => (
                              <li key={'investigation_requested_document' + index}>
                                <InvestigationUpload
                                  accountId={accountId ? accountId : -1}
                                  investigationId={participant.investigationId}
                                  requestedInvestigationDocument={requestedDocument}
                                  type={'investigation'}
                                  clearer={clearer}
                                  disabled={disabled}
                                  setDisabled={setDisabled}
                                />
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )
              )}
            </>
          ) : (
            <>
              <p>
                <strong>
                  <Text id="token-expired" />
                </strong>
              </p>
              <p>
                <Link className="btn btn-block btn-outline-dark" to="/logout#next=/auth">
                  <Text id="btns.sign-in-again" />
                </Link>
              </p>
            </>
          )}
        </Section>
      )}
      <Section>
        <p>
          <strong>
            <Text id="contact.content" />
          </strong>
          <br />
          <Text
            id="contact.content"
            textKey="description"
            siteGroup={siteGrpParam}
            data={{
              email: <ContactEmail siteGroup={siteGrpParam} />,
              phone: <ContactPhone siteGroup={siteGrpParam} />,
            }}
          />
        </p>
      </Section>
      <Section>
        {!authTokenExpired && (
          <p>
            <Link className="btn btn-block btn-primary btn-icon" to={getApplicationStepUrl('summary')}>
              <Text id="view-summary" page="status" />
            </Link>
          </p>
        )}
        {!isRnWebView && (
          <p>
            <Link className="btn btn-block btn-outline-dark" to="/logout">
              <Text id="btns.sign-out" />
            </Link>
          </p>
        )}
      </Section>
    </ApplicationLoader>
  );
};

export default StatusPage;
